import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Headset, Plus, X, Search, CheckCircle } from 'lucide-react';
import api from '../api';
import './UserManual.css';

const ServiceDeskPage = () => {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [issueType, setIssueType] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const COMMON_ISSUES = [
        "Login/Authentication Issue",
        "Course Assignment Error",
        "Profile Update",
        "Platform Bug/Error",
        "Other"
    ];

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await api.get('/tickets/my');
            setTickets(res.data);
        } catch (err) {
            console.error("Failed to fetch tickets", err);
            setError("Failed to load tickets.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    const handleMarkAsRead = async () => {
        try {
            await api.put('/tickets/mark-all-read');
            window.dispatchEvent(new Event('ticket_read_status_updated'));
            // Optionally, we could show a small toast or just let the user know
            setSuccessMessage("Marked all updates as read.");
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        
        if (!issueType || !description.trim()) {
            setError("Please fill out all fields.");
            return;
        }

        setSubmitting(true);
        try {
            const res = await api.post('/tickets/', {
                issue_type: issueType,
                description: description
            });
            setTickets([res.data, ...tickets]);
            setShowModal(false);
            setIssueType('');
            setDescription('');
            setSuccessMessage("Ticket submitted successfully!");
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            console.error("Failed to submit ticket", err);
            setError("Failed to submit ticket.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="user-manual-page">
            {/* Header Section from UserManual */}
            <div className="manual-header-banner">
                <div className="manual-header-content">
                    <div className="manual-title-row">
                        <div>
                            <div className="manual-title-badge">
                                <Headset size={14} /> LIVE SUPPORT DESK
                            </div>
                            <h1 className="manual-main-title">Service Desk</h1>
                            <p className="manual-subtitle">
                                Contact the support team and track your active issues.
                            </p>
                        </div>
                        <button 
                            onClick={() => navigate(-1)} 
                            style={{ 
                                cursor: 'pointer', 
                                background: 'linear-gradient(90deg, #0ea5e9 0%, #2563eb 100%)', 
                                color: 'white', 
                                border: 'none',
                                padding: '10px 24px',
                                borderRadius: '8px',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.3)'; }}
                        >
                            ← Return to Dashboard
                        </button>
                    </div>
                </div>
            </div>

            <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto', animation: 'fadeIn 0.3s ease' }}>
                {/* Actions Section */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '24px' }}>
                <button 
                    onClick={handleMarkAsRead}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: 'var(--bg-main)', color: 'var(--text-main)',
                        border: '1px solid var(--border)', padding: '10px 24px', borderRadius: '30px',
                        fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                        transition: 'all 0.2s ease', fontSize: '0.95rem'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.background = 'var(--bg-card)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-main)'; e.currentTarget.style.background = 'var(--bg-main)'; }}
                >
                    <CheckCircle size={18} /> Mark as Read
                </button>
                <button 
                    onClick={() => setShowModal(true)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: 'linear-gradient(135deg, var(--primary) 0%, #0284c7 100%)', color: 'white',
                        border: 'none', padding: '10px 24px', borderRadius: '30px',
                        fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease', fontSize: '0.95rem'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(14, 165, 233, 0.4)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.3)'; }}
                >
                    <Plus size={18} /> New Ticket
                </button>
            </div>

            {successMessage && (
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={20} /> {successMessage}
                </div>
            )}
            
            {error && (
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                    {error}
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading tickets...</div>
            ) : tickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                    <Headset size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
                    <p style={{ fontSize: '1.1rem' }}>No tickets found. Need help? Create a ticket!</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {tickets.map(ticket => (
                        <div 
                            key={ticket.id} 
                            style={{ 
                                background: 'var(--bg-card)', border: ticket.user_has_unread_response ? '2px solid var(--primary)' : '1px solid var(--border)', 
                                borderRadius: '16px', padding: '24px', transition: 'all 0.3s ease',
                                boxShadow: ticket.user_has_unread_response ? '0 8px 24px rgba(59, 130, 246, 0.15)' : '0 2px 10px rgba(0,0,0,0.03)'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {ticket.user_has_unread_response && (
                                        <span style={{ width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%', display: 'inline-block' }} title="Unread Response"></span>
                                    )}
                                    <h3 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.2rem', fontWeight: 600 }}>{ticket.issue_type}</h3>
                                </div>
                                <span style={{
                                    padding: '6px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.5px',
                                    background: ticket.status === 'OPEN' ? 'rgba(59, 130, 246, 0.1)' : ticket.status === 'RESOLVED' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                    color: ticket.status === 'OPEN' ? '#3b82f6' : ticket.status === 'RESOLVED' ? '#10b981' : '#f59e0b'
                                }}>
                                    {ticket.status}
                                </span>
                            </div>
                            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.6 }}>
                                {ticket.description}
                            </p>
                            
                            {ticket.admin_response && (
                                <div style={{ 
                                    marginTop: '20px', padding: '20px', background: 'rgba(14, 165, 233, 0.04)', 
                                    borderRadius: '0 12px 12px 0', borderLeft: '4px solid var(--primary)',
                                    position: 'relative'
                                }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 700 }}>
                                        Support Team Response
                                    </h4>
                                    <p style={{ margin: 0, color: 'var(--text-main)', fontSize: '1rem', lineHeight: 1.6 }}>{ticket.admin_response}</p>
                                </div>
                            )}
                            
                            <div style={{ marginTop: '20px', fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>Created on {new Date(ticket.created_at).toLocaleDateString()} at {new Date(ticket.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                <span>•</span>
                                <span>Ticket #{ticket.id}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Ticket Modal */}
            {showModal && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
                    <div className="modal-box" style={{ background: 'var(--bg-main)', width: '100%', maxWidth: '500px', borderRadius: '12px', overflow: 'hidden' }}>
                        <div style={{ padding: '20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.25rem' }}>Submit a Ticket</h2>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={24} /></button>
                        </div>
                        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-main)', fontWeight: 500 }}>Issue Type</label>
                                <select 
                                    value={issueType} 
                                    onChange={(e) => setIssueType(e.target.value)}
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '1rem' }}
                                    required
                                >
                                    <option value="" disabled>Select an issue...</option>
                                    {COMMON_ISSUES.map(issue => (
                                        <option key={issue} value={issue}>{issue}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-main)', fontWeight: 500 }}>Description</label>
                                <textarea 
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Please describe your problem in detail..."
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', minHeight: '120px', fontSize: '1rem', resize: 'vertical' }}
                                    required
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                                <button type="submit" disabled={submitting} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'var(--primary)', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
        </div>
    );
};

export default ServiceDeskPage;
